import React, { useState, useEffect, useRef, useContext } from 'react';
import DatePicker from 'react-datepicker';
import './Templates.css';
import { FaTimes, FaEnvelope, FaStar, FaCar, FaAngleDown, FaCalendarAlt, FaClock, FaChevronLeft, FaChevronRight, FaCircle, FaSyncAlt, FaPalette, FaChevronDown, FaChevronUp, FaArrowLeft, FaEye, FaEyeSlash } from 'react-icons/fa';
import { useToast } from './hooks/ToastContext';
import { UnifiedDateTimePicker } from './common/UnifiedDateTimePicker';
import { AuthContext } from './Provider';
import { useTranslation } from 'react-i18next';


/**
 * Event card detail component - displays an icon with descriptive text
 * Used in event cards to show location, date, time, and other event metadata
 *
 * @component
 * @example
 * <EventCardDetail
 *   icon={<FaMapMarkerAlt />}
 *   text="Rīga, Latvia"
 * />
 *
 * @param {React.ReactNode} icon - Icon element (typically from react-icons)
 * @param {string} text - Descriptive text to display next to icon
 */
export function EventCardDetail({ icon, text }) {
  return (
    <div className="event-card-detail">
      {icon}
      <span>{text}</span>
    </div>
  );
}

/**
 * Event card action button - primary button for event card interactions
 * Used for "Join Event", "View Details", and other event-related actions
 *
 * @component
 * @example
 * <EventCardButton
 *   label="Join Event"
 *   onClick={handleJoin}
 *   className="primary"
 * />
 *
 * @param {string} label - Button text to display
 * @param {function} onClick - Click handler function
 * @param {string} [className=''] - Additional CSS classes for styling
 */
export function EventCardButton({ label, onClick, className }) {
  return (
    <button className={`event-card-button ${className || ''}`} onClick={onClick}>
      <span>{label}</span>
    </button>
  );
}

/**
 * Primary action button (CustomButton1)
 * Simple button with clean styling for main user actions
 *
 * @component
 * @example
 * <CustomButton1 label="Submit" onClick={handleSubmit} />
 *
 * @param {string} label - Button text to display
 * @param {function} onClick - Click handler function
 * @alias Button.Primary - Preferred semantic export
 * @see {@link Button.Primary} for new usage
 */
export function CustomButton1({ label, onClick }) {
  return (
    <button className='Button1' onClick={onClick}>
      {label}
    </button>
  );
}

/**
 * Secondary action button (CustomButton2)
 * Button that supports additional children elements and disabled state
 * Use for secondary actions or buttons that need icons/badges
 *
 * @component
 * @example
 * <CustomButton2
 *   label="Cancel"
 *   onClick={handleCancel}
 *   disabled={isLoading}
 * >
 *   <FaTimes />
 * </CustomButton2>
 *
 * @param {string} label - Button text to display
 * @param {function} onClick - Click handler function
 * @param {React.ReactNode} [children] - Optional child elements (icons, badges)
 * @param {boolean} [disabled=false] - Whether button is disabled
 * @alias Button.Secondary - Preferred semantic export
 * @see {@link Button.Secondary} for new usage
 */
export function CustomButton2({ label, onClick, children, disabled }) {
  return (
    <button className='Button2' onClick={onClick} disabled={disabled}>
      {label}
      {children}
    </button>
  );
}

/**
 * Two-line label button (CustomButton3)
 * Button with two text labels displayed vertically
 * Use for buttons that need primary and secondary text (e.g., "Upload" + "Max 5MB")
 *
 * @component
 * @example
 * <CustomButton3
 *   label="Upload File"
 *   label2="Max 5MB"
 *   onClick={handleUpload}
 * />
 *
 * @param {string} label - Primary button text (bottom, larger)
 * @param {string} label2 - Secondary text (top, smaller)
 * @param {function} onClick - Click handler function
 * @alias Button.WithLabel - Preferred semantic export
 * @see {@link Button.WithLabel} for new usage
 */
export function CustomButton3({ label, label2, onClick }) {
  return (
    <button className='Button3' onClick={onClick}>
      <div className="IconContainer">
      <p className='B3text2'>{label2}</p>
      </div>
      <p className='B3text'>{label}</p>
    </button>
  );
 }

/**
 * Animated button with circular background effect (CustomButton4)
 * Eye-catching button with animated circle and arrow icon
 * Use for prominent "Next Step" or "Continue" actions
 *
 * @component
 * @example
 * <CustomButton4
 *   label="Next Step"
 *   onClick={handleNext}
 *   backgroundImage="/path/to/image.jpg"
 *   disabled={!isValid}
 * />
 *
 * @param {string} label - Button text to display
 * @param {function} onClick - Click handler function
 * @param {string} [backgroundImage] - URL for circle background image
 * @param {string} [backgroundColor] - Solid color for circle background
 * @param {boolean} [disabled=false] - Whether button is disabled (shows loading state)
 * @param {string} [className=''] - Additional CSS classes
 * @alias Button.Animated - Preferred semantic export
 * @see {@link Button.Animated} for new usage
 */
 export function CustomButton4({ label, onClick, backgroundImage, backgroundColor, disabled, className = '' }) {
  const buttonStyle = {
    ...(typeof backgroundImage === 'string' && { backgroundImage: `url(${backgroundImage})` }),
    ...(typeof backgroundColor !== 'undefined' && { backgroundColor: backgroundColor })
  };

  return (
    <button className={`Button4 ${disabled ? 'loading' : ''} ${className}`} onClick={onClick} disabled={disabled}>
      <span className="circle" style={buttonStyle}>
        <span className="icon arrow"></span>
      </span>
      <span className="button-text">{label}</span>
    </button>
  );
}

/**
 * Gradient button with background image support (CustomButton5)
 * Button with full background image/gradient and modern hover effects
 * Use for visually prominent actions that need background imagery
 *
 * @component
 * @example
 * <CustomButton5
 *   label="View Route"
 *   onClick={handleViewRoute}
 *   backgroundImage="/path/to/route-map.jpg"
 *   disabled={isLoading}
 * />
 *
 * @param {string} label - Button text to display
 * @param {function} onClick - Click handler function
 * @param {string} [backgroundImage] - URL for button background image
 * @param {boolean} [disabled=false] - Whether button is disabled (shows loading state)
 * @param {string} [className=''] - Additional CSS classes
 * @alias Button.Gradient - Preferred semantic export
 * @see {@link Button.Gradient} for new usage
 */
export function CustomButton5({ label, onClick, backgroundImage, disabled, className = '' }) {
  const buttonStyle = {
    ...(typeof backgroundImage === 'string' && { backgroundImage: `url(${backgroundImage})` }),
  };

  return (
    <button
      className={`Button5 ${disabled ? 'loading' : ''} ${className}`}
      onClick={onClick}
      disabled={disabled}
      style={buttonStyle}
    >
      <span className='sp1'>{label}</span>
      <span className='sp2'></span>
    </button>
  );
}

/**
 * Outlined button (CustomButton6)
 * Button with border outline and transparent background
 * Use for secondary actions that need less visual weight
 *
 * @component
 * @example
 * <CustomButton6 label="Learn More" onClick={handleLearnMore} />
 *
 * @param {string} label - Button text to display
 * @param {function} onClick - Click handler function
 * @param {string} type - Button type (submit, button, reset)
 * @param {boolean} disabled - Whether button is disabled
 * @alias Button.Outlined - Preferred semantic export
 * @see {@link Button.Outlined} for new usage
 */
export function CustomButton6({ label, onClick, type = 'button', disabled = false, className = '' }) {
  return (
    <button className={`Button6 ${className}`.trim()} onClick={onClick} type={type} disabled={disabled}>
      <div className='Button6-ins'>
        <span className='sp6'>
          {label}
        </span>
      </div>
    </button>
  );
}

/**
 * Button with badge/children overlay (CustomButton7)
 * Button that displays a badge or child element overlaid on the button
 * Use for notifications, counts, or status indicators on buttons
 *
 * @component
 * @example
 * <CustomButton7 label="Messages" onClick={handleMessages}>
 *   <span className="badge">5</span>
 * </CustomButton7>
 *
 * @param {string} label - Button text to display
 * @param {function} onClick - Click handler function
 * @param {React.ReactNode} [children] - Child elements (typically badges or counts)
 * @alias Button.WithBadge - Preferred semantic export
 * @see {@link Button.WithBadge} for new usage
 */
export function CustomButton7({children, label, onClick, disabled = false }) {
  return (
    <button className='Button7' onClick={onClick} disabled={disabled}>
      {label}
      <div className='Button7-ch'>{children}</div>
    </button>
  );
}

/**
 * Compact button (CustomButton8)
 * Smaller button with compact spacing for dense layouts
 * Use when space is limited or for less prominent actions
 *
 * @component
 * @example
 * <CustomButton8 label="Edit" onClick={handleEdit}>
 *   <FaPencil />
 * </CustomButton8>
 *
 * @param {string} label - Button text to display
 * @param {function} onClick - Click handler function
 * @param {React.ReactNode} [children] - Optional child elements (icons)
 * @alias Button.Compact - Preferred semantic export
 * @see {@link Button.Compact} for new usage
 */
export function CustomButton8({ children, label, onClick }) {
  return (
    <button className='Button8' onClick={onClick}>
      {label}
      {children}
    </button>
  );
}

/**
 * Button with icon positioned on right side (CustomButton9)
 * Default export button with text on left and icon on right
 * Use for navigation actions or buttons that indicate direction/next step
 *
 * @component
 * @example
 * <CustomButton9 label="Continue" onClick={handleContinue}>
 *   <FaArrowRight />
 * </CustomButton9>
 *
 * @param {string} label - Button text to display
 * @param {function} onClick - Click handler function
 * @param {React.ReactNode} [children] - Icon or element to display on right
 * @alias Button.IconRight - Preferred semantic export
 * @see {@link Button.IconRight} for new usage
 */
export default function CustomButton9({ label, onClick, children }) {
  return (
    <button className="button9 noselect" onClick={onClick}>
      <span className="text">{label}</span>
      <span className="icon">
        {children}
      </span>
    </button>
  );
}

/**
 * Large button (CustomButton10)
 * Larger button with prominent sizing for primary actions
 * Use for main CTAs or important actions that need visual emphasis
 *
 * @component
 * @example
 * <CustomButton10
 *   label="Get Started"
 *   onClick={handleGetStarted}
 *   disabled={!isReady}
 * >
 *   <FaRocket />
 * </CustomButton10>
 *
 * @param {string} label - Button text to display
 * @param {function} onClick - Click handler function
 * @param {React.ReactNode} [children] - Optional child elements (icons)
 * @param {boolean} [disabled=false] - Whether button is disabled
 * @alias Button.Large - Preferred semantic export
 * @see {@link Button.Large} for new usage
 */
export function CustomButton10({ label, onClick, children, disabled }) {
  return (
    <button className='Button10' onClick={onClick} disabled={disabled}>
      {label}
      {children}
    </button>
  );
}

/**
 * Cancel button variant (CustomButton10Ca)
 * Large button styled for cancel/dismiss actions
 * Use for dialog cancel buttons or actions that abandon changes
 *
 * @component
 * @example
 * <CustomButton10Ca
 *   label="Cancel"
 *   onClick={handleCancel}
 *   disabled={isProcessing}
 * />
 *
 * @param {string} label - Button text to display
 * @param {function} onClick - Click handler function
 * @param {React.ReactNode} [children] - Optional child elements (icons)
 * @param {boolean} [disabled=false] - Whether button is disabled
 * @alias Button.Cancel - Preferred semantic export
 * @see {@link Button.Cancel} for new usage
 */
export function CustomButton10Ca({ label, onClick, children, disabled, ariaLabel }) {
  return (
    <button className='Button10Ca' onClick={onClick} disabled={disabled} aria-label={ariaLabel}>
      {label}
      {children}
    </button>
  );
}

/**
 * Confirm button variant (CustomButton10Co)
 * Large button styled for confirmation/commit actions
 * Use for dialog confirm buttons or actions that finalize changes
 *
 * @component
 * @example
 * <CustomButton10Co
 *   label="Confirm"
 *   onClick={handleConfirm}
 *   disabled={!isValid}
 * />
 *
 * @param {string} label - Button text to display
 * @param {function} onClick - Click handler function
 * @param {React.ReactNode} [children] - Optional child elements (icons)
 * @param {boolean} [disabled=false] - Whether button is disabled
 * @alias Button.Confirm - Preferred semantic export
 * @see {@link Button.Confirm} for new usage
 */
export function CustomButton10Co({ label, onClick, children, disabled }) {
  return (
    <button className='Button10Co' onClick={onClick} disabled={disabled}>
      {label}
      {children}
    </button>
  );
}

/**
 * Danger/destructive action button (CustomButton11)
 * Button styled to indicate dangerous or irreversible actions
 * Use for delete, remove, or other destructive operations
 *
 * @component
 * @example
 * <CustomButton11
 *   label="Delete Account"
 *   onClick={handleDelete}
 *   disabled={!confirmed}
 * >
 *   <FaTrash />
 * </CustomButton11>
 *
 * @param {string} label - Button text to display
 * @param {function} onClick - Click handler function
 * @param {React.ReactNode} [children] - Optional child elements (icons)
 * @param {boolean} [disabled=false] - Whether button is disabled
 * @alias Button.Danger - Preferred semantic export
 * @see {@link Button.Danger} for new usage
 */
export function CustomButton11({ label, onClick, children, disabled }) {
  return (
    <button className='Button11' onClick={onClick} disabled={disabled}>
      {label}
      {children}
    </button>
  );
}
/**
 * Custom radio button component with styled appearance
 * Radio button with custom styling that matches the app design system
 *
 * @component
 * @example
 * <RadioButton1
 *   name="travelType"
 *   label="One-time"
 *   value="one-time"
 *   checked={travelType === 'one-time'}
 *   onChange={handleChange}
 * />
 *
 * @param {string} name - Input name attribute (groups radio buttons)
 * @param {string} label - Label text displayed next to radio button
 * @param {string} value - Value attribute for the radio input
 * @param {boolean} checked - Whether the radio button is checked
 * @param {function} onChange - Change handler function
 */
export function RadioButton1({ name, label, value, checked, onChange }) {
  return (
    <label className="RadioButton1">
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
      />
      <span className="RadioButton1-custom" />
      <span className="RadioButton1-label">{label}</span>
    </label>
  );
}

/**
 * Custom checkbox component with animated SVG checkmark
 * Checkbox with custom styling and smooth animation
 *
 * @component
 * @example
 * <Checkbox1
 *   id="terms"
 *   label="I agree to the terms and conditions"
 *   checked={agreedToTerms}
 *   onChange={handleCheckboxChange}
 *   onKeyDown={handleKeyPress}
 * />
 *
 * @param {string} id - Unique ID for the checkbox input
 * @param {string} label - Label text displayed next to checkbox
 * @param {boolean} checked - Whether the checkbox is checked
 * @param {function} onChange - Change handler function
 * @param {function} [onKeyDown] - Optional keyboard event handler
 */
export function Checkbox1({ id, label, checked, onChange, onKeyDown, className = '' }) {
  return (
    <div className={`CustomCheckBox1 ${className}`}>
      <input
        className="inp-cbx"
        id={id}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        onKeyDown={onKeyDown}
      />
      <label className="cbx" htmlFor={id}>
        <span className='spC1'>
          <svg className='svgC1' width="12px" height="12px">
            <use xlinkHref="#check-4" />
          </svg>
        </span>
        <span className='spC1'>{label}</span>
      </label>
      <svg className="inline-svg">
        <symbol id="check-4" viewBox="0 0 12 10">
          <polyline points="1.5 6 4.5 9 10.5 1"></polyline>
        </symbol>
      </svg>
    </div>
  );
}

/**
 * Text input with floating label animation
 * Input field where label floats up when field has value or focus
 * Commonly used for name/text inputs with clean UX
 *
 * @component
 * @example
 * <InputName1
 *   id="fullName"
 *   label="Full Name"
 *   value={name}
 *   onChange={(e) => setName(e.target.value)}
 * />
 *
 * @param {string} id - Unique ID for the input field
 * @param {string} label - Label text that floats when active
 * @param {string} value - Current input value
 * @param {function} onChange - Change handler function
 */
export function InputName1({ id, label, value, onChange }) {
  const hasValue = value && value.length > 0;

  return (
    <div className={`CustomInput1 ${hasValue ? 'has-value' : ''}`}>
      <input
        className='CI1'
        type="text"
        id={id}
        value={value}
        onChange={onChange}
        required
        autoComplete="off"
      />
      <label className='CL1' htmlFor={id}>{label}</label>
    </div>
  );
}

/**
 * Password input with show/hide toggle and optional floating label
 * Password field with security features, visibility toggle, and optional label display
 *
 * @component
 * @example
 * <InputPassword1
 *   id="password"
 *   label="Password"
 *   value={password}
 *   onChange={(e) => setPassword(e.target.value)}
 *   disabled={isLoading}
 * />
 *
 * @param {string} id - Unique ID for the input field
 * @param {string} [label] - Optional label text (floating label if provided)
 * @param {string} value - Current password value
 * @param {function} onChange - Change handler function
 * @param {boolean} [disabled=false] - Whether input is disabled
 */
export function InputPassword1({ id, label, value, onChange, disabled }) {
  const [showPassword, setShowPassword] = useState(false);

  // For the settings form, we might not have a label directly in this component
  const showInternalLabel = label !== undefined;

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className='CustomInput11'>
      <input
        className='CI11'
        type={showPassword ? "text" : "password"}
        id={id}
        value={value}
        onChange={onChange}
        disabled={disabled}
        required
        autoComplete="new-password"
      />
      {showInternalLabel && <label className='CL11' htmlFor={id}>{label}</label>}
      <button
        type="button"
        className="password-toggle-btn"
        onClick={togglePasswordVisibility}
        disabled={disabled}
        aria-label={showPassword ? "Hide password" : "Show password"}
        tabIndex={0}
      >
        {showPassword ? <FaEyeSlash /> : <FaEye />}
      </button>
    </div>
  );
}

/**
 * Versatile input box with icon support and focus states
 * General-purpose input field with support for icons, validation, and various input types
 * Features focus styling, error states, and optional constraints
 *
 * @component
 * @example
 * <CustomInputBox1
 *   placeholder="Enter your email"
 *   value={email}
 *   onChange={(e) => setEmail(e.target.value)}
 *   type="email"
 *   maxLength={100}
 *   error={emailError}
 *   required
 * >
 *   <FaEnvelope />
 * </CustomInputBox1>
 *
 * @param {string} placeholder - Placeholder text
 * @param {string} value - Current input value
 * @param {function} onChange - Change handler function
 * @param {React.ReactNode} [children] - Optional icon or prefix element
 * @param {string} [name] - Input name attribute
 * @param {string} [type='text'] - Input type (text, email, number, etc.)
 * @param {number} [maxLength] - Maximum character length
 * @param {boolean|string} [error] - Error state or error message
 * @param {number} [min] - Minimum value (for number inputs)
 * @param {number} [max] - Maximum value (for number inputs)
 * @param {boolean} [required=false] - Whether field is required
 * @param {boolean} [disabled=false] - Whether field is disabled
 */
export function CustomInputBox1({ placeholder, value, onChange, children, name, type = 'text', maxLength, error, min, max, required = false, disabled = false, className = '' }) {
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  return (
    <div className={`CustomInputBox1 ${isFocused || value ? 'focused' : ''} ${error ? 'error' : ''} ${className}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
      {children}
      <input
        className='CustomInput2'
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        required={required}
        disabled={disabled}
        placeholder={placeholder}
        maxLength={maxLength}
        min={min}
        max={max}
      />
      </div>
    </div>
  );
}

/**
 * Auto-resizing textarea with character counter
 * Textarea that automatically grows with content and displays remaining characters
 * Features focus states, character counting, and color-coded limit warnings
 *
 * @component
 * @example
 * <CustomTextArea
 *   placeholder="Describe your package..."
 *   value={description}
 *   onChange={(e) => setDescription(e.target.value)}
 *   maxLength={500}
 *   rows={3}
 *   disabled={isSubmitting}
 * />
 *
 * @param {string} placeholder - Placeholder text
 * @param {string} value - Current textarea value
 * @param {function} onChange - Change handler function
 * @param {number} [maxLength] - Maximum character length (enables counter)
 * @param {number} [rows=3] - Initial number of rows
 * @param {boolean} [disabled=false] - Whether textarea is disabled
 */
export function CustomTextArea({ placeholder, value, onChange, maxLength, rows = 3, disabled = false, className = '' }) {
  const [isFocused, setIsFocused] = useState(false);
  const [charCount, setCharCount] = useState(value ? value.length : 0);
  const textareaRef = useRef(null);

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleChange = (e) => {
    setCharCount(e.target.value.length);
    onChange(e);

    // Auto-resize functionality
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  // Auto-resize on mount if there's initial value
  useEffect(() => {
    if (textareaRef.current && value) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [value]);

  return (
    <div className={`custom-textarea-container ${isFocused || value ? 'focused' : ''} ${disabled ? 'disabled' : ''} ${className}`}>
      <textarea
        ref={textareaRef}
        className='custom-textarea'
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        maxLength={maxLength}
        rows={rows}
        disabled={disabled}
        style={{ resize: 'none', overflow: 'hidden' }}
      />
      {maxLength && (
        <div className="char-counter">
          <span className={charCount > maxLength * 0.8 ? charCount > maxLength * 0.95 ? 'near-limit' : 'approaching-limit' : ''}>
            {charCount}
          </span>
          <span>/</span>
          <span>{maxLength}</span>
        </div>
      )}
    </div>
  );
}

/**
 * Simple input box with keyboard support (CustomInputBox2)
 * Lightweight input field with focus states and keyboard event handling
 * Use for simple text inputs that need keyboard navigation
 *
 * @component
 * @example
 * <CustomInputBox2
 *   placeholder="Search..."
 *   value={searchTerm}
 *   onChange={(e) => setSearchTerm(e.target.value)}
 *   onKeyDown={handleKeyPress}
 * >
 *   <FaSearch />
 * </CustomInputBox2>
 *
 * @param {string} placeholder - Placeholder text
 * @param {string} value - Current input value
 * @param {function} onChange - Change handler function
 * @param {React.ReactNode} [children] - Optional icon or prefix element
 * @param {function} [onKeyDown] - Keyboard event handler
 */
export function CustomInputBox2({ placeholder, value, onChange, children, onKeyDown }) {
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  return (
    <div className={`CustomInputBox2 ${isFocused || value ? 'focused' : ''}`}>
      <div className='CI2-ins'>
        <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
        {children}
        <input
          className='CustomInput22'
          type="text"
          value={value}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={onKeyDown}
          required
          placeholder={placeholder}
        />
        </div>
      </div>
    </div>
  );
}
//---------------------NEXT-INPUT-BOX-------------------


//---------------------TOAST STYLE1-------------------
// In Templates.js, make sure these exports are present

/**
 * Toast notification component
 * Individual toast notification with auto-dismiss, manual close, and hover-to-pause
 * Features progress bar, type-based styling (success, error, warning, info), and smooth animations
 *
 * @component
 * @example
 * // Typically used via ToastContext, not directly
 * const { showToast } = useToast();
 * showToast('Success!', 'success');
 *
 * @param {string} type - Toast type (success, error, warning, info)
 * @param {string} title - Main toast message
 * @param {string} [description] - Optional secondary message
 * @param {function} onClose - Close handler function
 *
 * @description
 * Behavior:
 * - Auto-dismisses after 4.7 seconds
 * - Hover pauses auto-dismiss timer
 * - Manual close via X button
 * - Smooth slide-in and fade-out animations
 * - Accessible with proper ARIA roles
 */
export const Toast = ({ type, title, description, onClose }) => {
  const [isExiting, setIsExiting] = useState(false);
  const toastRef = useRef(null);

  // Handle auto-close with timer
  useEffect(() => {
    const timer = setTimeout(() => {
      handleClose();
    }, 4700); // Slightly less than CSS animation time

    return () => clearTimeout(timer);
  }, []);

  // Handle manual close with transition
  const handleClose = () => {
    // Set exiting state to trigger CSS transition
    setIsExiting(true);

    // Wait for transition to complete before actual removal
    setTimeout(() => {
      if (onClose) onClose();
    }, 300); // Match transition duration in CSS
  };

  // Pause animation on hover
  const handleMouseEnter = () => {
    if (toastRef.current) {
      toastRef.current.style.animationPlayState = 'paused';

      // Also pause progress bar animation
      const progressBar = toastRef.current.querySelector('.progress');
      if (progressBar) {
        progressBar.style.animationPlayState = 'paused';
      }
    }
  };

  // Resume animation on mouse leave
  const handleMouseLeave = () => {
    if (toastRef.current) {
      toastRef.current.style.animationPlayState = 'running';

      // Also resume progress bar animation
      const progressBar = toastRef.current.querySelector('.progress');
      if (progressBar) {
        progressBar.style.animationPlayState = 'running';
      }
    }
  };

  return (
    <div
      ref={toastRef}
      className={`toast-notification ${type} ${isExiting ? 'toast-exit' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role={type === 'error' ? 'alert' : 'status'}
    >
      <div className="toast-notification-content">
        <div className="toast-notification-title">{title}</div>
        {description && (
          <div className="toast-notification-description">{description}</div>
        )}
      </div>
      <button className="toast-notification-close" onClick={handleClose}>
        <FaTimes />
      </button>
      <div className="progress"></div>
    </div>
  );
};

/**
 * Toast notification container
 * Renders all active toast notifications in a fixed position
 * Manages toast lifecycle via ToastContext
 *
 * @component
 * @example
 * // Place once in App.js root
 * <ToastContainer />
 *
 * @requires useToast - Toast context hook for state management
 *
 * @description
 * - Fixed position (top-right by default)
 * - Stacks multiple toasts vertically
 * - Auto-manages toast removal
 * - Renders nothing when no toasts active
 */
export const ToastContainer = () => {
  const { toasts, removeToast } = useToast();

  if (!toasts.length) return null;

  return (
    <div className="toast-container">
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          {...toast}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  );
};

/**
 * DateTime picker container wrapper
 * Container for grouping date and time picker elements
 * Provides consistent spacing and layout for date/time selectors
 *
 * @component
 * @example
 * <DateTimePickerContainer>
 *   <DatePickerWrapper icon={<FaCalendar />}>
 *     <DatePicker ... />
 *   </DatePickerWrapper>
 *   <TimePickerWrapper icon={<FaClock />}>
 *     <TimePicker ... />
 *   </TimePickerWrapper>
 * </DateTimePickerContainer>
 *
 * @param {React.ReactNode} children - Child picker elements
 */
export function DateTimePickerContainer({ children }) {
  return (
    <div className="Templates-datetime-container">
      {children}
    </div>
  );
}

/**
 * Date picker wrapper with icon
 * Wrapper component for date picker inputs with consistent icon styling
 * Supports disabled state
 *
 * @component
 * @example
 * <DatePickerWrapper icon={<FaCalendarAlt />} disabled={false}>
 *   <DatePicker selected={date} onChange={setDate} />
 * </DatePickerWrapper>
 *
 * @param {React.ReactNode} icon - Icon element to display
 * @param {React.ReactNode} children - DatePicker component
 * @param {boolean} [disabled=false] - Whether picker is disabled
 * @param {React.Ref} ref - Forwarded ref for DatePicker integration
 */
export const DatePickerWrapper = React.forwardRef(({ icon, children, disabled = false }, ref) => {
  return (
    <div ref={ref} className={`Templates-date-picker-wrapper ${disabled ? 'disabled' : ''}`}>
      <div className="icon">
        {icon}
      </div>
      {children}
    </div>
  );
});

/**
 * Time picker wrapper with icon
 * Wrapper component for time picker inputs with consistent icon styling
 * Supports disabled state
 *
 * @component
 * @example
 * <TimePickerWrapper icon={<FaClock />} disabled={false}>
 *   <TimePicker selected={time} onChange={setTime} />
 * </TimePickerWrapper>
 *
 * @param {React.ReactNode} icon - Icon element to display
 * @param {React.ReactNode} children - TimePicker component
 * @param {boolean} [disabled=false] - Whether picker is disabled
 * @param {React.Ref} ref - Forwarded ref for TimePicker integration
 */
export const TimePickerWrapper = React.forwardRef(({ icon, children, disabled = false }, ref) => {
  return (
    <div ref={ref} className={`Templates-time-picker-wrapper ${disabled ? 'disabled' : ''}`}>
      <div className="icon">
        {icon}
      </div>
      {children}
    </div>
  );
});

/**
 * Modern form section with icon and title
 * Section wrapper for grouping related form fields
 * Features icon support and consistent styling
 *
 * @component
 * @example
 * <ModernFormSection icon={<FaUser />} title="Personal Information">
 *   <InputName1 ... />
 *   <CustomInputBox1 ... />
 * </ModernFormSection>
 *
 * @param {React.ReactNode} [icon] - Optional icon to display before title
 * @param {string} title - Section heading text
 * @param {React.ReactNode} children - Form fields to display in section
 * @param {string} [className=''] - Additional CSS classes
 */
export function ModernFormSection({ icon, title, children, className = '' }) {
  return (
    <section className={`form-section ${className}`}>
      <h3>
        {icon && <span className="section-icon">{icon}</span>}
        {title}
      </h3>
      {children}
    </section>
  );
}

/**
 * Segmented two-or-more-option toggle (chip pair). Both options are always
 * visible (unlike a switch that shows only the active label), so the choice is
 * self-explanatory. For genuine on/off booleans, use ModernSwitch instead.
 *
 * @param {{value:string,label:string,icon?:React.ReactNode}[]} options
 * @param {string} value - currently selected option value
 * @param {function} onChange - called with the newly selected value
 */
export function SegmentedToggle({ options = [], value, onChange, disabled = false, className = '' }) {
  return (
    <div className={`ws-segmented ${disabled ? 'disabled' : ''} ${className}`} role="group">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          className={`ws-seg-btn ${value === opt.value ? 'selected' : ''}`}
          onClick={() => !disabled && onChange(opt.value)}
          disabled={disabled}
          aria-pressed={value === opt.value}
        >
          {opt.icon && <span className="ws-seg-icon">{opt.icon}</span>}
          <span>{opt.label}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * Modern card container
 * Card wrapper with shadow, rounded corners, and optional hover effect
 * Use for grouping content in visually distinct containers
 *
 * @component
 * @example
 * <ModernCard hover={true} className="profile-card">
 *   <h2>Profile</h2>
 *   <p>User information...</p>
 * </ModernCard>
 *
 * @param {React.ReactNode} children - Card content
 * @param {string} [className=''] - Additional CSS classes
 * @param {boolean} [hover=true] - Enable hover lift effect
 */
export function ModernCard({ children, className = '', hover = true }) {
  return (
    <div className={`modern-card ${className} ${hover ? 'hoverable' : ''}`}>
      {children}
    </div>
  );
}

/**
 * Modern button (WSButton) — the single on-brand button used across Profile,
 * Settings and other modernized screens. Renders only `wsform-btn` classes (no
 * legacy .ButtonN base), so styling never fights the old button rules.
 *
 * @param {'primary'|'secondary'|'danger'|'icon'} [variant='primary']
 * @param {string} [label] - visible text
 * @param {React.ReactNode} [icon] - leading icon (or the only content for variant="icon")
 * @param {React.ReactNode} [iconRight] - trailing icon
 * @param {boolean} [danger] - with variant="icon", tints the circular button red
 * @param {boolean} [block] - full width
 * @param {string} [ariaLabel] - REQUIRED for icon-only buttons (no visible label)
 */
export function WSButton({
  variant = 'primary',
  label,
  icon,
  iconRight,
  onClick,
  disabled = false,
  type = 'button',
  ariaLabel,
  title,
  danger = false,
  block = false,
  className = '',
}) {
  const classes = [
    'wsform-btn',
    `wsform-btn--${variant}`,
    variant === 'icon' && danger ? 'wsform-btn--danger' : '',
    block ? 'wsform-btn--block' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <button
      type={type}
      className={classes}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel || undefined}
      title={title || ariaLabel || undefined}
    >
      {icon && <span className="wsform-btn__icon">{icon}</span>}
      {label && <span className="wsform-btn__label">{label}</span>}
      {iconRight && <span className="wsform-btn__icon">{iconRight}</span>}
    </button>
  );
}

//---------------------MODERN DROPDOWN-------------------
/**
 * Modern dropdown/select component with custom styling
 * Dropdown with animated arrow, click-outside handling, and active state highlighting
 * Features optional label with icon, placeholder, and keyboard-accessible options
 *
 * @component
 * @example
 * <ModernDropdown
 *   label="Select Country"
 *   icon={<FaGlobe />}
 *   value={selectedCountry}
 *   options={['Latvia', 'Estonia', 'Lithuania']}
 *   onSelect={(country) => setSelectedCountry(country)}
 *   placeholder="Choose country"
 * />
 *
 * @param {string} [label] - Optional label displayed above dropdown
 * @param {string} [value] - Currently selected value
 * @param {Array<string>} options - Array of dropdown options
 * @param {function} onSelect - Callback when option is selected
 * @param {React.ReactNode} [icon] - Optional icon displayed next to label
 * @param {string} [placeholder='Select option'] - Placeholder text when no value selected
 * @param {boolean} [disabled=false] - Whether dropdown is disabled
 * @param {string} [className=''] - Additional CSS classes
 *
 * @description
 * Features:
 * - Click outside to close dropdown
 * - Animated arrow rotation on open/close
 * - Active option highlighting
 * - Automatic state management
 */
export function ModernDropdown({
  label,
  value,
  options,
  onSelect,
  icon,
  placeholder = 'Select option',
  disabled = false,
  className = ''
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`modern-dropdown ${className} ${isOpen ? 'open' : ''}`} ref={dropdownRef}>
      {label && (
        <h4 className="dropdown-label">
          {icon && <span className="label-icon">{icon}</span>}
          {label}
        </h4>
      )}
      <div className="dropdown-control">
        <button 
          type="button"
          className="modern-dropdown-button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
        >
          <span>{value || placeholder}</span>
          <FaAngleDown className={`dropdown-arrow ${isOpen ? 'open' : ''}`} />
        </button>
        
        {isOpen && (
          <ul className="dropdown-list">
            {options.map((option, index) => (
              <li 
                key={index}
                className={value === option ? 'active' : ''}
                onClick={() => {
                  onSelect(option);
                  setIsOpen(false);
                }}
              >
                {option}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

//---------------------MODERN SWITCH-------------------
/**
 * Modern toggle switch component with icon support
 * Animated switch/toggle with left/right icons that change based on checked state
 * Features smooth sliding animation and disabled state styling
 *
 * @component
 * @example
 * <ModernSwitch
 *   label="Dark Mode"
 *   checked={isDarkMode}
 *   onChange={(e) => setIsDarkMode(e.target.checked)}
 *   leftIcon={<FaSun />}
 *   rightIcon={<FaMoon />}
 * />
 *
 * @param {string} [label] - Label text displayed next to switch
 * @param {boolean} checked - Whether switch is in checked/on state
 * @param {function} onChange - Callback when switch is toggled
 * @param {React.ReactNode} [leftIcon] - Icon displayed when unchecked
 * @param {React.ReactNode} [rightIcon] - Icon displayed when checked
 * @param {boolean} [disabled=false] - Whether switch is disabled
 * @param {string} [className=''] - Additional CSS classes
 *
 * @description
 * Features:
 * - Smooth sliding animation
 * - Dynamic icon switching based on state
 * - Accessible checkbox input
 * - Disabled state with opacity styling
 */
export function ModernSwitch({
  label,
  checked,
  onChange,
  leftIcon,
  rightIcon,
  disabled = false,
  className = ''
}) {
  return (
    <label className={`modern-switch ${className} ${disabled ? 'disabled' : ''}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <div className="slider">
        <span className="slider-icon">
          {checked ? rightIcon : leftIcon}
        </span>
      </div>
      <span className="switch-label">{label}</span>
    </label>
  );
}

//---------------------MODERN DATE TIME PICKER-------------------
/**
 * Modern date and time picker component
 * Wrapper around UnifiedDateTimePicker with default theme
 * Provides combined date + time selection in a single component
 *
 * @component
 * @example
 * <ModernDateTimePicker
 *   dateValue="2025-01-15"
 *   timeValue="14:30"
 *   onDateChange={(newDate) => setTravelDate(newDate)}
 *   onTimeChange={(newTime) => setTravelTime(newTime)}
 *   disabled={false}
 * />
 *
 * @param {string} dateValue - Date value in YYYY-MM-DD format
 * @param {string} timeValue - Time value in HH:MM format
 * @param {function} onDateChange - Callback when date changes
 * @param {function} onTimeChange - Callback when time changes
 * @param {boolean} [disabled=false] - Whether picker is disabled
 *
 * @description
 * This is a wrapper component that uses UnifiedDateTimePicker internally.
 * Uses "default" theme styling. For custom themes, use UnifiedDateTimePicker directly.
 *
 * @requires UnifiedDateTimePicker
 */
export function ModernDateTimePicker({
  dateValue,
  timeValue,
  onDateChange,
  onTimeChange,
  disabled = false
}) {
  return (
    <UnifiedDateTimePicker
      dateValue={dateValue}
      timeValue={timeValue}
      onDateChange={onDateChange}
      onTimeChange={onTimeChange}
      disabled={disabled}
      theme="default"
    />
  );
}


/**
 * Add stop/waypoint button for route planning
 * Button to add additional stops in multi-point routes
 * Features icon, text label, and hover effect
 *
 * @component
 * @example
 * <AddStopButton
 *   onClick={handleAddStop}
 *   disabled={maxStopsReached}
 *   text="Add Stop"
 * >
 *   <FaPlus />
 * </AddStopButton>
 *
 * @param {function} onClick - Click handler function
 * @param {boolean} [disabled=false] - Whether button is disabled
 * @param {React.ReactNode} [children] - Icon element to display
 * @param {string} [text='Add'] - Button text label
 */
export function AddStopButton({ onClick, disabled = false, children, text = 'Add' }) {
  return (
    <button
      className={`add-stop-button ${disabled ? 'disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      <span className="add-stop-icon">
        {children}
      </span>
      <span className="add-stop-text">{text}</span>
      <div className="add-stop-hover-effect"></div>
    </button>
  );
}

/**
 * Directional navigation button for carousels and tooltips
 * Generic navigation button with direction support (prev/next, left/right, up/down)
 * Use for carousels, image galleries, paginated content
 *
 * @component
 * @example
 * <NavButton
 *   direction="next"
 *   onClick={handleNext}
 *   disabled={isLastSlide}
 *   title="Next slide"
 * >
 *   <FaChevronRight />
 * </NavButton>
 *
 * @param {string} direction - Button direction (prev, next, up, down, left, right)
 * @param {function} onClick - Click handler function
 * @param {boolean} [disabled=false] - Whether button is disabled
 * @param {string} [className=''] - Additional CSS classes
 * @param {string} [title] - Tooltip text on hover
 * @param {React.ReactNode} [children] - Icon or content to display
 */
export function NavButton({ direction, onClick, disabled = false, className = '', title, children }) {
  return (
    <button
      className={`nav-button ${direction} ${disabled ? 'disabled' : ''} ${className}`}
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      {children}
    </button>
  );
}

/**
 * Refresh/reload button with spinning icon
 * Button that shows a spinning refresh icon when disabled (loading state)
 * Use for data refresh, reload, or sync operations
 *
 * @component
 * @example
 * <RefreshButton
 *   onClick={handleRefresh}
 *   disabled={isRefreshing}
 *   tooltip="Refresh data"
 *   size="large"
 * />
 *
 * @param {function} onClick - Click handler function
 * @param {boolean} [disabled=false] - Whether button is disabled (shows spinning icon)
 * @param {string} [tooltip] - Tooltip text on hover
 * @param {string} [className=''] - Additional CSS classes
 * @param {string} [size='medium'] - Button size (small, medium, large)
 */
export function RefreshButton({ onClick, disabled = false, tooltip, className = '', size = 'medium' }) {
  return (
    <button
      className={`refresh-button ${size} ${disabled ? 'disabled' : ''} ${className}`}
      onClick={onClick}
      disabled={disabled}
      title={tooltip}
    >
      <FaSyncAlt className={`refresh-icon ${disabled ? 'spinning' : ''}`} />
    </button>
  );
}

/**
 * Complete pagination navigation container
 * Full-featured pagination control with prev/next buttons and page indicator
 * Supports compact mode and dot indicators for image carousels
 *
 * @component
 * @example
 * <NavigationContainer
 *   currentPage={currentPage}
 *   totalPages={10}
 *   onPrevClick={handlePrev}
 *   onNextClick={handleNext}
 *   showDots={false}
 *   compact={false}
 * />
 *
 * @param {number} currentPage - Current page index (zero-based)
 * @param {number} totalPages - Total number of pages
 * @param {function} onPrevClick - Handler for previous button
 * @param {function} onNextClick - Handler for next button
 * @param {boolean} [disabled=false] - Disable all navigation
 * @param {string} [className=''] - Additional CSS classes
 * @param {boolean} [compact=false] - Use compact layout
 * @param {boolean} [showDots=false] - Show dot indicators instead of page numbers
 */
export function NavigationContainer({ currentPage, totalPages, onPrevClick, onNextClick, disabled = false, className = '', compact = false, showDots = false }) {
  const containerClass = `navigation-container ${compact ? 'compact' : ''} ${className}`;

  return (
    <div className={containerClass}>
      <NavButton
        direction="prev"
        onClick={onPrevClick}
        disabled={disabled || currentPage === 0}
        title="Previous page (or swipe right)"
      >
        <FaChevronLeft />
      </NavButton>

      {showDots ? (
        <div className="page-indicators">
          {Array.from({ length: totalPages }, (_, index) => (
            <FaCircle
              key={index}
              className={`indicator ${currentPage === index ? 'active' : ''}`}
            />
          ))}
        </div>
      ) : (
        <div className="page-info">
          <span className="page-number">{currentPage + 1}</span>
          <span className="page-divider">/</span>
          <span className="total-pages">{totalPages}</span>
        </div>
      )}

      <NavButton
        direction="next"
        onClick={onNextClick}
        disabled={disabled || currentPage === totalPages - 1}
        title="Next page (or swipe left)"
      >
        <FaChevronRight />
      </NavButton>
    </div>
  );
}

/**
 * Inline theme selector dropdown
 * Dropdown to switch between application themes (Default, Dark, Light, Cyber)
 * Integrates with AuthContext for theme persistence
 * Automatically updates user preferences in database
 *
 * @component
 * @example
 * <InlineThemeSelector />
 *
 * @requires AuthContext - Uses theme state and changeTheme function
 * @requires useTranslation - For internationalized theme names
 */
export function InlineThemeSelector() {
  const { t } = useTranslation('common');
  const { userProfile, updateThemePreference } = useContext(AuthContext);
  const [isExpanded, setIsExpanded] = useState(false);

  // SIMPLIFIED: Just track selection, don't apply theme (Provider handles that)
  const [selectedOption, setSelectedOption] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    console.log('InlineThemeSelector initialized with theme:', savedTheme || 'option1');
    return savedTheme || 'option1';
  });

  const handleThemeChange = (option) => {
    console.log('User selected theme:', option);
    setSelectedOption(option);
    if (updateThemePreference) {
      updateThemePreference(option);
    }
    // Close the theme selector after selecting a theme
    setIsExpanded(false);
  };

  const toggleThemeSelector = () => {
    setIsExpanded(!isExpanded);
  };

  // Get the current theme name for display
  const getCurrentThemeName = () => {
    switch(selectedOption) {
      case 'option1':
        return t('menu.theme_default');
      case 'option2':
        return t('menu.theme_dark');
      case 'option3':
        return t('menu.theme_light');
      default:
        return t('menu.theme_default');
    }
  };

  return (
    <div className="inline-theme-selector">
      <button
        className="theme-toggle-button"
        onClick={toggleThemeSelector}
        type="button"
      >
        <FaPalette className="theme-icon" />
        <span className="theme-current">{getCurrentThemeName()}</span>
        {isExpanded ? <FaChevronUp className="chevron-icon" /> : <FaChevronDown className="chevron-icon" />}
      </button>

      {isExpanded && (
        <div className="theme-option-group">
          <label className="theme-option">
            <input
              type="radio"
              name="theme"
              value="option1"
              checked={selectedOption === 'option1'}
              onChange={() => handleThemeChange('option1')}
            />
            <span className="theme-label">{t('menu.theme_default')}</span>
          </label>
          <label className="theme-option">
            <input
              type="radio"
              name="theme"
              value="option2"
              checked={selectedOption === 'option2'}
              onChange={() => handleThemeChange('option2')}
            />
            <span className="theme-label">{t('menu.theme_dark')}</span>
          </label>
          <label className="theme-option">
            <input
              type="radio"
              name="theme"
              value="option3"
              checked={selectedOption === 'option3'}
              onChange={() => handleThemeChange('option3')}
            />
            <span className="theme-label">{t('menu.theme_light')}</span>
          </label>
        </div>
      )}
    </div>
  );
}

//---------------------CLOSE BUTTONS-------------------
/**
 * Close button variant 1 - Icon-based (Legacy)
 * Simple close button with FaTimes icon
 *
 * @component
 * @deprecated Use Button.Close instead for new code
 * @example
 * <CloseButton1 onClick={handleClose} size="medium" />
 *
 * @param {function} onClick - Click handler function
 * @param {string} [className=''] - Additional CSS classes
 * @param {string} [size='medium'] - Button size (small/medium/large)
 */
export function CloseButton1({ onClick, className = '', size = 'medium' }) {
  return (
    <button
      className={`close-button-1 ${size} ${className}`}
      onClick={onClick}
      type="button"
      aria-label="Close"
    >
      <FaTimes />
    </button>
  );
}

/**
 * Close button variant 2 - Animated X (Legacy)
 * Close button with two animated lines forming an X
 *
 * @component
 * @deprecated Use Button.Close instead for new code
 * @example
 * <CloseButton2 onClick={handleClose} size="large" />
 *
 * @param {function} onClick - Click handler function
 * @param {string} [className=''] - Additional CSS classes
 * @param {string} [size='medium'] - Button size (small/medium/large)
 */
export function CloseButton2({ onClick, className = '', size = 'medium' }) {
  return (
    <button
      className={`close-button-2 ${size} ${className}`}
      onClick={onClick}
      type="button"
      aria-label="Close"
    >
      <span className="close-line-1"></span>
      <span className="close-line-2"></span>
    </button>
  );
}

/**
 * Close button variant 3 - Wrapped icon (Legacy)
 * Close button with FaTimes icon wrapped in container
 *
 * @component
 * @deprecated Use Button.Close instead for new code
 * @example
 * <CloseButton3 onClick={handleClose} className="modal-close" />
 *
 * @param {function} onClick - Click handler function
 * @param {string} [className=''] - Additional CSS classes
 * @param {string} [size='medium'] - Button size (small/medium/large)
 */
export function CloseButton3({ onClick, className = '', size = 'medium' }) {
  return (
    <button
      className={`close-button-3 ${size} ${className}`}
      onClick={onClick}
      type="button"
      aria-label="Close"
    >
      <div className="close-icon-wrapper">
        <FaTimes />
      </div>
    </button>
  );
}

//---------------------SEARCH BUTTONS-------------------
export function SearchButton1({ onClick, className = '', size = 'medium' }) {
  return (
    <button
      className={`search-btn-1 ${size} ${className}`}
      onClick={onClick}
      type="button"
      aria-label="Search"
    >
      <svg className="search-icon-1" viewBox="0 0 24 24" fill="none">
        <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
        <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    </button>
  );
}

export function SearchButton2({ onClick, className = '', size = 'medium' }) {
  return (
    <button
      className={`search-btn-2 ${size} ${className}`}
      onClick={onClick}
      type="button"
      aria-label="Search"
    >
      <svg className="search-icon-2" viewBox="0 0 24 24" fill="none">
        <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
        <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    </button>
  );
}

export function SearchButton3({ onClick, className = '', size = 'medium' }) {
  const { t } = useTranslation('common');

  return (
    <button
      className={`search-btn-3 ${size} ${className}`}
      onClick={onClick}
      type="button"
    >
      <svg className="search-icon-3" viewBox="0 0 24 24" fill="none">
        <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
        <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
      <span className="search-text-3">{t('search')}</span>
    </button>
  );
}

export function SearchButton4({ onClick, className = '', size = 'medium' }) {
  const { t } = useTranslation('common');

  return (
    <button
      className={`search-btn-4 ${size} ${className}`}
      onClick={onClick}
      type="button"
    >
      <svg className="search-icon-4" viewBox="0 0 24 24" fill="none">
        <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
        <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
      <span className="search-text-4">{t('search')}</span>
    </button>
  );
}

//---------------------BACK BUTTONS-------------------
/**
 * Back button variant 1 - Icon only (Legacy)
 * Simple back button with arrow-left SVG icon
 *
 * @component
 * @deprecated Use BackButton instead for new code
 * @example
 * <BackButton1 onClick={() => navigate(-1)} size="medium" />
 *
 * @param {function} onClick - Click handler function
 * @param {string} [className=''] - Additional CSS classes
 * @param {string} [size='medium'] - Button size (small/medium/large)
 */
export function BackButton1({ onClick, className = '', size = 'medium' }) {
  return (
    <button
      className={`back-btn-1 ${size} ${className}`}
      onClick={onClick}
      type="button"
      aria-label="Go back"
    >
      <svg className="back-icon-1" viewBox="0 0 24 24" fill="none">
        <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </button>
  );
}

/**
 * Back button variant 2 - Icon with label (Legacy)
 * Back button with chevron-left icon and optional/translated text label
 *
 * @component
 * @deprecated Use BackButton instead for new code
 * @example
 * <BackButton2 onClick={() => navigate(-1)} label="Go Back" />
 *
 * @param {function} onClick - Click handler function
 * @param {string} [className=''] - Additional CSS classes
 * @param {string} [size='medium'] - Button size (small/medium/large)
 * @param {string} [label] - Optional custom label (defaults to translated "Back")
 */
export function BackButton2({ onClick, className = '', size = 'medium', label }) {
  const { t } = useTranslation('common');

  return (
    <button
      className={`back-btn-2 ${size} ${className}`}
      onClick={onClick}
      type="button"
      aria-label="Go back"
    >
      <svg className="back-icon-2" viewBox="0 0 24 24" fill="none">
        <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      {label && <span className="back-text-2">{label}</span>}
      {!label && <span className="back-text-2">{t('buttons.back') || 'Back'}</span>}
    </button>
  );
}

/**
 * Back button variant 3 - Wrapped icon (Legacy)
 * Back button with FaArrowLeft icon wrapped in container
 *
 * @component
 * @deprecated Use BackButton instead for new code
 * @example
 * <BackButton3 onClick={() => navigate(-1)} className="header-back" />
 *
 * @param {function} onClick - Click handler function
 * @param {string} [className=''] - Additional CSS classes
 * @param {string} [size='medium'] - Button size (small/medium/large)
 */
export function BackButton3({ onClick, className = '', size = 'medium' }) {
  return (
    <button
      className={`back-btn-3 ${size} ${className}`}
      onClick={onClick}
      type="button"
      aria-label="Go back"
    >
      <div className="back-icon-wrapper-3">
        <FaArrowLeft />
      </div>
    </button>
  );
}

/**
 * Back button variant 4 - Chevron with label (Legacy)
 * Back button with FaChevronLeft icon and optional/translated text label
 *
 * @component
 * @deprecated Use BackButton instead for new code
 * @example
 * <BackButton4 onClick={() => navigate(-1)} label="Return" size="large" />
 *
 * @param {function} onClick - Click handler function
 * @param {string} [className=''] - Additional CSS classes
 * @param {string} [size='medium'] - Button size (small/medium/large)
 * @param {string} [label] - Optional custom label (defaults to translated "Back")
 */
export function BackButton4({ onClick, className = '', size = 'medium', label }) {
  const { t } = useTranslation('common');

  return (
    <button
      className={`back-btn-4 ${size} ${className}`}
      onClick={onClick}
      type="button"
      aria-label="Go back"
    >
      <div className="back-content-4">
        <FaChevronLeft className="back-chevron-4" />
        {label && <span className="back-text-4">{label}</span>}
        {!label && <span className="back-text-4">{t('buttons.back') || 'Back'}</span>}
      </div>
    </button>
  );
}

//---------------------CHAT INPUT-------------------
/**
 * Auto-expanding chat message input
 * Textarea input for chat messages with auto-resize (max 3 lines) and Enter-to-send
 * Features: automatic height adjustment, Shift+Enter for new line, character limit
 *
 * @component
 * @example
 * <ChatInput
 *   value={message}
 *   onChange={(e) => setMessage(e.target.value)}
 *   onSend={handleSendMessage}
 *   placeholder="Type a message..."
 *   maxLength={2000}
 *   disabled={isSending}
 * />
 *
 * @param {string} value - Current message text
 * @param {function} onChange - Change handler function
 * @param {function} onSend - Send handler (called on Enter key or send button click)
 * @param {string} [placeholder='Type a message...'] - Placeholder text
 * @param {boolean} [disabled=false] - Whether input is disabled
 * @param {number} [maxLength=2000] - Maximum character length
 *
 * @description
 * Keyboard behavior:
 * - Enter: Send message
 * - Shift+Enter: New line
 * - Auto-expands up to 3 lines, then scrolls
 */
export function ChatInput({ value, onChange, onSend, placeholder = 'Type a message...', disabled = false, maxLength = 2000, sendLabel = 'Send message' }) {
  const textareaRef = useRef(null);
  const [rows, setRows] = useState(1);

  useEffect(() => {
    if (textareaRef.current) {
      // Reset height to auto to get the correct scrollHeight
      textareaRef.current.style.height = 'auto';

      const scrollHeight = textareaRef.current.scrollHeight;
      const lineHeight = 24; // approximate line height in pixels
      const maxHeight = lineHeight * 3; // 3 lines max before scrolling

      if (scrollHeight <= maxHeight) {
        textareaRef.current.style.height = `${scrollHeight}px`;
        textareaRef.current.style.overflowY = 'hidden';
        setRows(Math.min(3, Math.ceil(scrollHeight / lineHeight)));
      } else {
        textareaRef.current.style.height = `${maxHeight}px`;
        textareaRef.current.style.overflowY = 'auto';
        setRows(3);
      }
    }
  }, [value]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (value.trim() && onSend) {
        onSend();
      }
    }
  };

  return (
    <div className="chat-input-container">
      <textarea
        ref={textareaRef}
        className="chat-input-textarea"
        value={value}
        onChange={onChange}
        onKeyPress={handleKeyPress}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        rows={1}
      />
      <button
        className={`chat-send-button ${value.trim() ? 'active' : ''}`}
        onClick={onSend}
        disabled={disabled || !value.trim()}
        type="button"
        aria-label={sendLabel}
      >
        <svg className="send-icon" viewBox="0 0 24 24" fill="none">
          <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </div>
  );
}

/* ============================================================================
 * SEMANTIC COMPONENT EXPORTS - Phase 2A
 * ============================================================================
 *
 * New semantic names for better developer experience.
 * Old names (CustomButton1, etc.) remain for backward compatibility.
 *
 * Migration Guide: src/readme/COMPONENT_NAMING_MAP.md
 *
 * @since January 2025
 * ============================================================================ */

/**
 * Primary action button component
 *
 * Use for the main call-to-action on a page or form.
 *
 * @component
 * @example
 * ```jsx
 * <Button.Primary label="Submit" onClick={handleSubmit} />
 * ```
 *
 * @param {string} label - Button text to display
 * @param {function} onClick - Click handler function
 *
 * @alias CustomButton1
 */
export const Button = {
  /**
   * Primary action button
   * @type {typeof CustomButton1}
   */
  Primary: CustomButton1,

  /**
   * Secondary action button with optional children
   * @type {typeof CustomButton2}
   */
  Secondary: CustomButton2,

  /**
   * Button with two-line label display
   * @type {typeof CustomButton3}
   */
  WithLabel: CustomButton3,

  /**
   * Animated button with circular background effect
   * Use for primary actions needing visual emphasis
   * @type {typeof CustomButton4}
   */
  Animated: CustomButton4,

  /**
   * Button with gradient or background image support
   * @type {typeof CustomButton5}
   */
  Gradient: CustomButton5,

  /**
   * Outlined style button
   * @type {typeof CustomButton6}
   */
  Outlined: CustomButton6,

  /**
   * Button with badge or children overlay
   * @type {typeof CustomButton7}
   */
  WithBadge: CustomButton7,

  /**
   * Compact button style
   * @type {typeof CustomButton8}
   */
  Compact: CustomButton8,

  /**
   * Button with icon positioned on right side
   * @type {typeof CustomButton9}
   */
  IconRight: CustomButton9,

  /**
   * Large size button
   * @type {typeof CustomButton10}
   */
  Large: CustomButton10,

  /**
   * Cancel action button variant
   * @type {typeof CustomButton10Ca}
   */
  Cancel: CustomButton10Ca,

  /**
   * Confirm action button variant
   * @type {typeof CustomButton10Co}
   */
  Confirm: CustomButton10Co,

  /**
   * Danger/destructive action button
   * Use for delete, remove, or other destructive actions
   * @type {typeof CustomButton11}
   */
  Danger: CustomButton11,
};

/**
 * Consolidated BackButton component
 *
 * Replaces BackButton1, BackButton2, BackButton3, BackButton4 with a single unified component.
 * All old BackButton variants are now wrappers around this component.
 *
 * @component
 * @example
 * ```jsx
 * // Simple back button
 * <BackButton onClick={goBack} />
 *
 * // With label
 * <BackButton onClick={goBack} label="Go Back" showLabel />
 *
 * // Large size
 * <BackButton onClick={goBack} size="large" />
 * ```
 *
 * @param {'small' | 'medium' | 'large'} [size='medium'] - Button size
 * @param {function} onClick - Click handler function
 * @param {string} [label=''] - Optional label text
 * @param {boolean} [showLabel=false] - Whether to display the label
 */
export function BackButton({
  size = 'medium',
  onClick,
  label = '',
  showLabel = false
}) {
  return (
    <button
      className={`back-button back-button--${size}`}
      onClick={onClick}
      aria-label={label || 'Go back'}
    >
      <FaArrowLeft />
      {showLabel && label && <span className="back-button__label">{label}</span>}
    </button>
  );
}

/**
 * Consolidated SearchButton component
 *
 * Replaces SearchButton1, SearchButton2, SearchButton3, SearchButton4 with a single unified component.
 *
 * @component
 * @example
 * ```jsx
 * // Icon-only search button
 * <SearchButton variant="icon" onClick={handleSearch} />
 *
 * // Search input with clear
 * <SearchButton
 *   variant="input"
 *   value={searchTerm}
 *   onChange={setSearchTerm}
 *   onClear={clearSearch}
 *   placeholder="Search..."
 * />
 *
 * // Button with custom children
 * <SearchButton variant="button" onClick={handleSearch}>
 *   <span>Advanced Search</span>
 * </SearchButton>
 * ```
 *
 * @param {'icon' | 'input' | 'button'} [variant='icon'] - Search button variant
 * @param {function} [onClick] - Click handler (for icon/button variants)
 * @param {string} [value] - Search input value (for input variant)
 * @param {function} [onChange] - Input change handler (for input variant)
 * @param {function} [onClear] - Clear button handler (for input variant)
 * @param {string} [placeholder='Search...'] - Input placeholder (for input variant)
 * @param {React.ReactNode} [children] - Custom content (for button variant)
 */
export function SearchButton({
  variant = 'icon',
  onClick,
  value,
  onChange,
  onClear,
  placeholder = 'Search...',
  children
}) {
  if (variant === 'input') {
    return (
      <div className="search-input-container">
        <input
          type="text"
          className="search-input"
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
        />
        {value && (
          <button
            className="search-clear-button"
            onClick={onClear}
            aria-label="Clear search"
          >
            <FaTimes />
          </button>
        )}
        <button
          className="search-submit-button"
          onClick={onClick}
          aria-label="Search"
        >
          🔍
        </button>
      </div>
    );
  }

  if (variant === 'button') {
    return (
      <button className="search-button-custom" onClick={onClick}>
        {children}
      </button>
    );
  }

  // Default: icon variant
  return (
    <button
      className="search-button-icon"
      onClick={onClick}
      aria-label="Search"
    >
      🔍
    </button>
  );
}